import {useCallback, useEffect, useRef, useState} from 'react'
import {InteractionRequiredAuthError, InteractionStatus} from '@azure/msal-browser'
import {useMsal} from '@azure/msal-react'
import { MSAL_ACCESS_TOKEN_KEY } from '@/utils/constants/globalConstants'
import { ROUTES } from '@/utils/constants/routesConstants'
import { loginRequest } from '@/helpers/msalConfig'

const interactionRequiredCodes = new Set(['interaction_required', 'invalid_grant', 'no_tokens_found'])

export const useAuth = ({shellActive = false} = {}) => {
  const {instance, accounts, inProgress} = useMsal()
  const account = accounts[0]
  const [tokenReady, setTokenReady] = useState(shellActive)
  const loginStartedRef = useRef(false)

  const signOut = useCallback(async () => {
    localStorage.removeItem(MSAL_ACCESS_TOKEN_KEY)
    setTokenReady(false)
    return instance.logoutRedirect({postLogoutRedirectUri: ROUTES.HOME})
  }, [instance])

  const login = useCallback(async () => {
    if (!shellActive && inProgress === InteractionStatus.None && !loginStartedRef.current) {
      loginStartedRef.current = true
      try {
        return await instance.loginRedirect(loginRequest)
      } catch (error) {
        loginStartedRef.current = false
        console.error('Error starting MSAL login:', error)
        throw error
      }
    }
    return undefined
  }, [inProgress, instance, shellActive])

  useEffect(() => {
    if (shellActive) {
      setTokenReady(true)
      return
    }

    if (!account || inProgress !== InteractionStatus.None) {
      setTokenReady(false)
      return
    }

    let active = true

    const acquireToken = async () => {
      try {
        const response = await instance.acquireTokenSilent({...loginRequest, account})
        localStorage.setItem(MSAL_ACCESS_TOKEN_KEY, response.idToken)
        if (active) setTokenReady(true)
      } catch (error) {
        const requiresInteraction =
          error instanceof InteractionRequiredAuthError || interactionRequiredCodes.has(error?.errorCode)

        if (requiresInteraction) {
          await instance.acquireTokenRedirect({...loginRequest, account})
          return
        }

        console.error('Error acquiring MSAL token:', error)
        if (active) await signOut()
      }
    }

    acquireToken()

    return () => {
      active = false
    }
  }, [account, inProgress, instance, shellActive, signOut])

  const hasStandaloneAccount = Boolean(account)
  const isAuthenticated = shellActive || (hasStandaloneAccount && tokenReady)
  const isReady = shellActive || tokenReady || (!hasStandaloneAccount && inProgress === InteractionStatus.None)

  return {
    isReady,
    isAuthenticated,
    authState: inProgress,
    account,
    claims: account?.idTokenClaims ?? {},
    login,
    signOut,
  }
}
